import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import HelpTooltip from '../../components/HelpTooltip';

const LIBRARY_CATEGORIES = [
  'Ball mastery',
  'Dribbling and 1v1 moves',
  'First touch and receiving',
  'Passing and wall work',
  'Shooting and finishing',
  'Juggling and aerial control',
  'Speed/agility/footwork',
  'Goalkeeping',
];

const AGE_RANGES = [
  { label: 'Under 10', value: 'Under 10' },
  { label: '10\u201313', value: '10-13' },
  { label: '14+', value: '14+' },
];

function getYouTubeThumbnail(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
  return null;
}

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (match) return `https://www.youtube.com/embed/${match[1]}`;
  return null;
}

function truncateDescription(desc, wordCount = 18) {
  if (!desc) return '';
  const words = desc.split(/\s+/);
  if (words.length <= wordCount) return desc;
  return words.slice(0, wordCount).join(' ') + '...';
}

const CATEGORY_IMAGES = {
  'Ball mastery': { slug: 'ball-mastery', count: 2 },
  'Dribbling and 1v1 moves': { slug: 'dribbling-and-1v1-moves', count: 2 },
  'First touch and receiving': { slug: 'first-touch-and-receiving', count: 2 },
  'Passing and wall work': { slug: 'passing-and-wall-work', count: 2 },
  'Shooting and finishing': { slug: 'shooting-and-finishing', count: 2 },
  'Juggling and aerial control': { slug: 'juggling-and-aerial-control', count: 2 },
  'Speed/agility/footwork': { slug: 'speed-agility-and-footwork', count: 5 },
  'Goalkeeping': { slug: 'goalkeeping', count: 2 },
};

function getCategoryThumbnail(category, variant) {
  const info = CATEGORY_IMAGES[category];
  if (!info) return null;
  const idx = variant % info.count;
  const suffix = idx === 0 ? '' : String(idx + 1);
  return `/library/${info.slug}${suffix}.png`;
}

export default function Library() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPaid, setIsPaid] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedAgeRanges, setSelectedAgeRanges] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Video modal
  const [videoDrill, setVideoDrill] = useState(null);

  const buildQuery = useCallback((off = 0) => {
    const params = new URLSearchParams();
    if (showNew) params.set('new', '1');
    if (search.trim()) params.set('search', search.trim());
    if (selectedCategories.length > 0) params.set('categories', selectedCategories.join('|'));
    if (selectedAgeRanges.length === 1) params.set('age_range', selectedAgeRanges[0]);
    else if (selectedAgeRanges.length === 2) {
      params.set('age_ranges', selectedAgeRanges.join(','));
    }
    if (!showNew) params.set('offset', off);
    return params.toString();
  }, [search, selectedCategories, selectedAgeRanges, showNew]);

  const loadDrills = useCallback(async (append = false) => {
    const off = append ? offset + 20 : 0;
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const query = buildQuery(off);
      const data = await apiFetch(`/api/library?${query}`);
      if (append) {
        setDrills(prev => [...prev, ...data.drills]);
      } else {
        setDrills(data.drills);
      }
      setHasMore(data.hasMore);
      setOffset(off);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [apiFetch, buildQuery, offset]);

  // Initial load and reload on filter change
  useEffect(() => {
    setOffset(0);
    loadDrills(false);
  }, [search, selectedAgeRanges, selectedCategories, showNew]); // eslint-disable-line

  useEffect(() => { apiFetch('/api/admin/is-paid').then(d => setIsPaid(d.isPaid)).catch(() => {}); }, [apiFetch]);

  // For free coaches: interleave premium drills at positions 1, 5, then random 3-8 intervals
  const displayDrills = useMemo(() => {
    if (isPaid) return drills;
    const premium = drills.filter(d => d.is_premium);
    const regular = drills.filter(d => !d.is_premium);
    if (premium.length === 0) return regular;

    const result = [];
    let pi = 0;
    let ri = 0;
    let nextPremiumAt = 0; // position 0 = first slot
    const premiumPositions = [0, 4]; // 1st and 5th (0-indexed)
    let posAfterFixed = 4;

    for (let pos = 0; ri < regular.length || pi < premium.length; pos++) {
      const shouldInsertPremium = pi < premium.length && (
        premiumPositions.includes(pos) ||
        (pos > posAfterFixed && pos >= nextPremiumAt)
      );
      if (shouldInsertPremium) {
        result.push(premium[pi++]);
        if (pos > posAfterFixed) {
          nextPremiumAt = pos + 3 + Math.floor(Math.random() * 6); // 3-8 gap
        }
      } else if (ri < regular.length) {
        result.push(regular[ri++]);
      } else if (pi < premium.length) {
        result.push(premium[pi++]);
      } else {
        break;
      }
    }
    return result;
  }, [drills, isPaid]);

  const handleLoadMore = () => {
    loadDrills(true);
  };

  const toggleAgeRange = (val) => {
    setSelectedAgeRanges(prev => prev.includes(val) ? prev.filter(a => a !== val) : [...prev, val]);
  };

  const toggleCategory = (cat) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const handleAddDrill = (drill) => {
    if (!isPaid && drill.is_premium) return;
    navigate('/admin/drills', { state: { prefill: { title: drill.title, description: drill.description, youtube_url: drill.youtube_url } } });
  };

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  return (
    <div className="admin-page">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>Drill Library</h1>
        <HelpTooltip>
          <h3>Drill Library</h3>
          <p>Browse TrainDaily's collection of drills, each with a video tutorial. Use the search bar to find drills by name, or filter by age range and category to narrow things down.</p>
          <p>Click any drill to preview the video. When you find one you want to assign, head to the Drills page and use "Add Drill" to schedule it for your team.</p>
        </HelpTooltip>
      </div>

      {/* Filter Bar */}
      <div className="library-filters">
        <div className="library-filter-row">
          <input
            className="form-input"
            placeholder="Search drills..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <button
            className={`btn btn-sm ${showNew ? 'btn-orange' : 'btn-outline'}`}
            onClick={() => setShowNew(prev => !prev)}
          >
            {'\uD83D\uDD25'} New
          </button>
          <div style={{ display: 'flex', gap: 6 }}>
            {AGE_RANGES.map(ar => (
              <button
                key={ar.value}
                className={`btn btn-sm ${selectedAgeRanges.includes(ar.value) ? 'btn-orange' : 'btn-outline'}`}
                onClick={() => toggleAgeRange(ar.value)}
              >
                {ar.label}
              </button>
            ))}
          </div>
          <div style={{ position: 'relative' }}>
            <button
              className={`btn btn-sm ${selectedCategories.length > 0 ? 'btn-orange' : 'btn-outline'}`}
              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            >
              Category{selectedCategories.length > 0 ? ` (${selectedCategories.length})` : ''}
            </button>
            {showCategoryDropdown && (
              <div className="category-dropdown">
                {LIBRARY_CATEGORIES.map(cat => (
                  <label key={cat} className="category-dropdown-item">
                    <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={() => toggleCategory(cat)} />
                    {cat}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : (
        <>
          <div className="library-grid">
            {(() => { const catCounters = {}; return displayDrills.map(drill => {
              const pc = drill.primary_category || (drill.categories && drill.categories[0]);
              const variant = pc ? (catCounters[pc] = (catCounters[pc] || 0) + 1, catCounters[pc] - 1) : 0;
              const catThumb = pc ? getCategoryThumbnail(pc, variant) : null;
              const premiumLocked = !isPaid && drill.is_premium;
              return (
              <div key={drill.id} className="library-card" style={premiumLocked ? { opacity: 0.5, position: 'relative' } : undefined}>
                {premiumLocked && (
                  <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 2, background: 'rgba(247,124,0,0.9)', color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700 }}>
                    <i className="fas fa-lock" style={{ marginRight: 4 }}></i>Premium
                  </div>
                )}
                {drill.youtube_url && getYouTubeThumbnail(drill.youtube_url) ? (
                  <img
                    src={getYouTubeThumbnail(drill.youtube_url)}
                    alt={drill.title}
                    className="library-thumbnail"
                    onClick={() => !premiumLocked && setVideoDrill(drill)}
                    style={{ cursor: premiumLocked ? 'default' : 'pointer' }}
                  />
                ) : catThumb ? (
                  <img
                    src={catThumb}
                    alt={pc}
                    className="library-thumbnail"
                  />
                ) : (
                  <div className="library-thumbnail library-thumbnail-placeholder">
                    No Video
                  </div>
                )}
                <div className="library-card-body">
                  <h3 className="library-card-title">{drill.title}</h3>
                  <p className="library-card-desc">{truncateDescription(drill.description)}</p>
                  <div className="library-tags">
                    {(drill.age_ranges || []).map(ar => (
                      <span key={ar} className="library-tag age-tag">{ar}</span>
                    ))}
                    {(drill.categories || []).map(cat => (
                      <span key={cat} className="library-tag category-tag">{cat}</span>
                    ))}
                  </div>
                  {premiumLocked ? (
                    <a href="/admin/upgrade?from=library" style={{ display: 'inline-block', marginTop: 8, fontSize: '0.8rem', color: 'var(--orange)', fontWeight: 600 }}>
                      <i className="fas fa-lock" style={{ marginRight: 4 }}></i>Upgrade to use →
                    </a>
                  ) : (
                    <button className="btn btn-sm btn-orange" onClick={() => handleAddDrill(drill)} style={{ marginTop: 8 }}>
                      + Add as Drill
                    </button>
                  )}
                </div>
              </div>
              );
            }); })()}
          </div>

          {drills.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: 32 }}>No drills found matching your filters.</p>
          )}

          {hasMore && !showNew && (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button className="btn btn-outline" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Video Playback Modal */}
      {videoDrill && (
        <div className="modal-overlay" onClick={() => setVideoDrill(null)}>
          <div className="video-modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, color: 'var(--white)' }}>{videoDrill.title}</h3>
              <button className="btn btn-sm btn-outline" onClick={() => setVideoDrill(null)}>Close</button>
            </div>
            <div className="video-container">
              <iframe
                src={getYouTubeEmbedUrl(videoDrill.youtube_url)}
                title={videoDrill.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
